import * as React from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/api/api';
import { queryKeys } from '@/libs/queryKeys';
import type { EchoLikeStatsResponse } from '@/types/ratings';
import type { EchoPopularityStats } from '@/types/echo';

export { BatchedEchoStatsContext } from './BatchedEchoStatsContext';
export { BatchedEchoStatsProvider } from './BatchedEchoStatsProvider';

/**
 * Bulk-fetch stats for a list of echo IDs in a single backend request.
 *
 * Returns filter-friendly `statsMap` / `popularityMap` plus a full
 * `statsByEchoId` map suitable for `<BatchedEchoStatsProvider>`.
 */
export const useAllEchoStats = (echoIds: string[]) => {
  const queryClient = useQueryClient();

  // Sort+dedupe so React Query treats reorderings as the same cache key.
  const sortedIds = React.useMemo(() => {
    const unique = Array.from(new Set(echoIds.filter(Boolean)));
    unique.sort();
    return unique;
  }, [echoIds]);

  const { data, isLoading } = useQuery({
    queryKey: queryKeys.stats.echoesBatch(sortedIds),
    queryFn: async () => {
      const result = await api.ratings.getEchoesStatsBatch(sortedIds);
      // Mirror to per-echo cache so consumers outside the provider tree hit the cached batch result.
      Object.entries(result).forEach(([id, stats]) => {
        queryClient.setQueryData(queryKeys.stats.echo(id), stats);
      });
      return result;
    },
    enabled: sortedIds.length > 0,
  });

  // Maps are derived from a referentially-stable React Query `data` snapshot,
  // so memoizing here keeps consumer `useMemo` deps stable across renders.
  const { statsMap, popularityMap, statsByEchoId } = React.useMemo(() => {
    const result = {
      statsMap: new Map<string, number>(),
      popularityMap: new Map<string, EchoPopularityStats>(),
      statsByEchoId: new Map<string, EchoLikeStatsResponse>(),
    };
    if (data) {
      Object.entries(data).forEach(([echoId, stats]) => {
        const typed = stats as EchoLikeStatsResponse;
        result.statsMap.set(echoId, typed.total_super_likes);
        result.popularityMap.set(echoId, {
          totalSuperLikes: typed.total_super_likes,
          totalMessages: typed.total_messages,
        });
        result.statsByEchoId.set(echoId, typed);
      });
    }
    return result;
  }, [data]);

  return { statsMap, popularityMap, statsByEchoId, isLoading };
};
